import { useProductCardConsumerLayoutContext } from 'product-card/hooks/useProductCardConsumerLayoutContext'
import { useProductCardFocused } from 'product-card/hooks/useProductCardFocused/useProductCardFocused'
import { useProductImageHovered } from 'product-card/hooks/useProductImageHovered/useProductImageHovered'
import {
	createContext,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'

interface ProductCardInteractionContextProps {
	isSizePickerVisible: boolean
	toggleSizePickerVisibility: () => void
	setSizePickerVisibilityToggle: (value: boolean) => void
	handleMouseEnter: () => void
	handleMouseLeave: () => void
}

interface ProductCardInteractionProviderProps {
	readonly children: ReactNode
}

export const ProductCardInteractionContext =
	createContext<ProductCardInteractionContextProps | null>(null)

export const ProductCardInteractionProvider = ({
	children,
}: ProductCardInteractionProviderProps) => {
	const { isLargeOrGreater, isTouchable } = useResponsive()

	/**
	 * Focus / hover control
	 */
	const { isProductImageHovered, handleMouseEnter, handleMouseLeave } =
		useProductImageHovered()

	const isProductCardFocused = useProductCardFocused()

	const isUserInteractingWithProductCard =
		isProductCardFocused || isProductImageHovered

	/**
	 * Manual size picker visibility toggle for large and touchable devices
	 */
	const { hasOverlayAddToBag } = useProductCardConsumerLayoutContext()
	const hasSizePickerVisibilityToggle =
		isLargeOrGreater && isTouchable && hasOverlayAddToBag

	const [sizePickerVisibilityToggle, setSizePickerVisibilityToggle] =
		useState(false)

	const toggleSizePickerVisibility = useCallback(() => {
		setSizePickerVisibilityToggle((prev) => !prev)
	}, [])

	const values = useMemo(
		() => ({
			isSizePickerVisible: hasSizePickerVisibilityToggle
				? sizePickerVisibilityToggle
				: isUserInteractingWithProductCard,
			handleMouseEnter,
			handleMouseLeave,
			toggleSizePickerVisibility,
			setSizePickerVisibilityToggle,
		}),
		[
			sizePickerVisibilityToggle,
			hasSizePickerVisibilityToggle,
			isUserInteractingWithProductCard,
			handleMouseEnter,
			handleMouseLeave,
			toggleSizePickerVisibility,
		]
	)

	useEffect(() => {
		// If toggle is active but the user clicks outside the product card, size picker must hide
		if (!isUserInteractingWithProductCard) {
			setSizePickerVisibilityToggle(false)
		}
	}, [isUserInteractingWithProductCard])

	return (
		<ProductCardInteractionContext.Provider value={values}>
			{children}
		</ProductCardInteractionContext.Provider>
	)
}
